'use client';

/**
 * Router Configuration for RWA Terminal
 *
 * This module defines all application routes with:
 * - Lazy loading for code splitting and optimal bundle size
 * - Role-based access control via ProtectedRoute wrapper
 * - Default redirects based on user role
 *
 * Route Structure:
 * - /login           - Public, authentication page
 * - /dashboard       - All roles, summary dashboard
 * - /assets          - All roles, asset list with filters
 * - /assets/:assetId - All roles, asset detail with 10-tab workflow
 * - /tasks           - Analyst+, work queue for pending items
 * - /snapshots       - All roles, cross-asset snapshot overview
 * - /risk-pricing    - All roles, cross-asset risk & pricing
 * - /judgements      - All roles, judgement list & review queue
 * - /audit-logs      - All roles, global audit trail
 * - /alerts          - All roles, alert rules & notification history
 * - /admin/users     - Publisher only, user management
 */

import { lazy, Suspense, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
} from 'react-router-dom';
import { ProtectedRoute, ALLOWED_ROLES } from '@/components/common/ProtectedRoute';
import { useAuthStore } from '@/stores/authStore';
import { DEFAULT_LANDING_PAGE, type Role } from '@/types';

// =============================================================================
// Lazy Loaded Page Components
// =============================================================================
// Using React.lazy for code splitting - each page loads only when needed

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const AssetsPage = lazy(() => import('@/pages/AssetsPage'));
const AssetDetailPage = lazy(() => import('@/pages/AssetDetailPage'));
const TasksPage = lazy(() => import('@/pages/TasksPage'));
const SnapshotsPage = lazy(() => import('@/pages/SnapshotsPage'));
const RiskPricingPage = lazy(() => import('@/pages/RiskPricingPage'));
const JudgementsPage = lazy(() => import('@/pages/JudgementsPage'));
const AuditLogsPage = lazy(() => import('@/pages/AuditLogsPage'));
const AdminUsersPage = lazy(() => import('@/pages/AdminUsersPage'));
const CrossAssetPage = lazy(() => import('@/pages/CrossAssetPage'));
const AlertsPage = lazy(() => import('@/pages/AlertsPage'));
const IntakePage = lazy(() => import('@/pages/IntakePage'));
const SignalsPage = lazy(() => import('@/pages/SignalsPage'));

// Placeholder pages for 404 and Unauthorized states
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('@/pages/UnauthorizedPage'));

// =============================================================================
// Loading Fallback Component
// =============================================================================

/**
 * Full-page loading spinner displayed while lazy components are loading
 */
function PageLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Loading...
        </p>
      </div>
    </div>
  );
}

/**
 * Wrapper component that adds Suspense boundary around lazy-loaded pages
 */
function SuspenseWrapper({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      {children}
    </Suspense>
  );
}

// =============================================================================
// Root Redirect Component
// =============================================================================

/**
 * Handles the root route redirect based on authentication state and role
 *
 * - If not authenticated: redirects to /login
 * - If authenticated: redirects to the role-appropriate default page
 */
function RootRedirect() {
  const user = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);

  // Show loading state while auth is being initialized
  if (isLoading) {
    return <PageLoadingFallback />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to role-specific default landing page
  const defaultPage = DEFAULT_LANDING_PAGE[user.role as Role] || '/assets';
  return <Navigate to={defaultPage} replace />;
}

// =============================================================================
// Layout Component
// =============================================================================

/**
 * Root layout wrapper that provides common structure for all authenticated routes
 * The Layout component with Sidebar and Header is loaded lazily
 */
const Layout = lazy(() => import('@/components/common/Layout'));

/**
 * Authenticated layout wrapper that includes the common layout structure
 */
function AuthenticatedLayout() {
  return (
    <SuspenseWrapper>
      <Layout>
        <SuspenseWrapper>
          <Outlet />
        </SuspenseWrapper>
      </Layout>
    </SuspenseWrapper>
  );
}

// =============================================================================
// Route Definitions
// =============================================================================

/**
 * Application route configuration
 *
 * Route access levels:
 * - Public: No authentication required (login)
 * - All Roles: Any authenticated user can access
 * - Analyst+: Analysts, Reviewers, and Publishers can access
 * - Publisher Only: Only Publishers (admin) can access
 */
const routes: RouteObject[] = [
  // ==========================================================================
  // Public Routes
  // ==========================================================================
  {
    path: '/login',
    element: (
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    ),
  },

  // ==========================================================================
  // Protected Routes with Layout
  // ==========================================================================
  {
    element: <AuthenticatedLayout />,
    children: [
      // Dashboard - All authenticated users
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ALL}>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },

      // Asset List - All authenticated users
      {
        path: '/assets',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ALL}>
            <AssetsPage />
          </ProtectedRoute>
        ),
      },

      // Asset Detail - All authenticated users
      {
        path: '/assets/:assetId',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ALL}>
            <AssetDetailPage />
          </ProtectedRoute>
        ),
      },

      // Tasks/Workbench - Analyst and above (not readonly)
      {
        path: '/tasks',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ANALYST_AND_ABOVE}>
            <TasksPage />
          </ProtectedRoute>
        ),
      },

      // Snapshots Overview - All authenticated users
      {
        path: '/snapshots',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ALL}>
            <SnapshotsPage />
          </ProtectedRoute>
        ),
      },

      // Risk & Pricing Overview - All authenticated users
      {
        path: '/risk-pricing',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ALL}>
            <RiskPricingPage />
          </ProtectedRoute>
        ),
      },

      // Judgements - All authenticated users
      {
        path: '/judgements',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ALL}>
            <JudgementsPage />
          </ProtectedRoute>
        ),
      },

      // Audit Logs - All authenticated users
      {
        path: '/audit-logs',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ALL}>
            <AuditLogsPage />
          </ProtectedRoute>
        ),
      },

      // Cross-Asset Comparison - All authenticated users
      {
        path: '/cross-asset',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ALL}>
            <CrossAssetPage />
          </ProtectedRoute>
        ),
      },

      // Alerts & Notifications - All authenticated users
      {
        path: '/alerts',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ALL}>
            <AlertsPage />
          </ProtectedRoute>
        ),
      },

      // Intake shortlist - Analyst and above; the tick is publisher-only inside the page
      {
        path: '/intake',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ANALYST_AND_ABOVE}>
            <IntakePage />
          </ProtectedRoute>
        ),
      },

      // Signals - Analyst and above; approve/reject are publisher-only inside the page
      {
        path: '/signals',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.ANALYST_AND_ABOVE}>
            <SignalsPage />
          </ProtectedRoute>
        ),
      },

      // Admin Users - Publisher only
      {
        path: '/admin/users',
        element: (
          <ProtectedRoute allowedRoles={ALLOWED_ROLES.PUBLISHER_ONLY}>
            <AdminUsersPage />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // ==========================================================================
  // Error and Utility Routes
  // ==========================================================================
  {
    path: '/unauthorized',
    element: (
      <SuspenseWrapper>
        <UnauthorizedPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/404',
    element: (
      <SuspenseWrapper>
        <NotFoundPage />
      </SuspenseWrapper>
    ),
  },

  // ==========================================================================
  // Root and Catch-All Routes
  // ==========================================================================
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
];

// =============================================================================
// Router Instance
// =============================================================================

/**
 * Create the browser router instance
 * Uses createBrowserRouter from React Router v7 for data-aware routing
 */
export const router = createBrowserRouter(routes);

/**
 * Export routes for testing or server-side rendering scenarios
 */
export { routes };

/**
 * Route path constants for type-safe navigation
 * Use these with useNavigate() for consistent routing
 */
export const ROUTES = {
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  ASSETS: '/assets',
  ASSET_DETAIL: (assetId: string) => `/assets/${assetId}`,
  TASKS: '/tasks',
  SNAPSHOTS: '/snapshots',
  RISK_PRICING: '/risk-pricing',
  JUDGEMENTS: '/judgements',
  AUDIT_LOGS: '/audit-logs',
  CROSS_ASSET: '/cross-asset',
  ALERTS: '/alerts',
  INTAKE: '/intake',
  SIGNALS: '/signals',
  ADMIN_USERS: '/admin/users',
  UNAUTHORIZED: '/unauthorized',
  NOT_FOUND: '/404',
} as const;

/**
 * Helper function to get asset detail URL with optional tab
 *
 * @param assetId - The asset ID
 * @param tab - Optional tab name to navigate to (e.g., 'conflicts', 'validation')
 * @returns The URL string with optional tab query parameter
 *
 * @example
 * getAssetDetailUrl('asset-123') // '/assets/asset-123'
 * getAssetDetailUrl('asset-123', 'conflicts') // '/assets/asset-123?tab=conflicts'
 */
export function getAssetDetailUrl(assetId: string, tab?: string): string {
  const baseUrl = ROUTES.ASSET_DETAIL(assetId);
  if (tab) {
    return `${baseUrl}?tab=${tab}`;
  }
  return baseUrl;
}

/**
 * Asset detail tab names for type-safe tab navigation
 */
export const ASSET_TABS = {
  OVERVIEW: 'overview',
  DOCUMENTS_FACTS: 'documents-facts',
  EVENTS: 'events',
  CONFLICTS: 'conflicts',
  VALIDATION: 'validation',
  CONFIDENCE: 'confidence',
  SNAPSHOTS: 'snapshots',
  RISK_PRICING: 'risk-pricing',
  JUDGEMENTS: 'judgements',
  COMPLIANCE: 'compliance',
  AUDIT: 'audit',
} as const;

export type AssetTab = (typeof ASSET_TABS)[keyof typeof ASSET_TABS];
